import { ChevronDown, Download } from "lucide-react"
import { useState } from "react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

export const DOWNLOAD_FORMATS = ["csv", "xlsx", "pdf"] as const
export type DownloadFormat = (typeof DOWNLOAD_FORMATS)[number]

interface Props {
  // Base filename without extension — used to seed the input on open.
  defaultFilename: string
  // Builds the server URL for a given format. The dialog appends
  // `?filename=<sanitized custom name>` (without extension) before
  // navigating. The server's DownloadableReport concern falls back to
  // its own default if the param is blank/missing.
  buildHref: (format: DownloadFormat) => string
  // Optional: hide formats that don't make sense for a given surface.
  // Defaults to all three.
  formats?: readonly DownloadFormat[]
  // Disable trigger button (e.g. invoicing's empty-month case).
  disabled?: boolean
  triggerLabel?: string
  // When true, offer a "split into one file per customer" checkbox for the
  // PDF format. The download then yields a ZIP of per-customer PDFs (the
  // dialog appends `&split=customer` to the href). Only meaningful for
  // surfaces with a customer axis (always for invoicing; for reports only
  // when the report has a customer column).
  canSplit?: boolean
}

const FORMAT_LABEL: Record<DownloadFormat, string> = {
  csv: "CSV",
  xlsx: "XLSX",
  pdf: "PDF",
}

export default function DownloadReportDialog({
  defaultFilename,
  buildHref,
  formats = DOWNLOAD_FORMATS,
  disabled = false,
  triggerLabel = "Download",
  canSplit = false,
}: Props) {
  const [open, setOpen] = useState(false)
  const [filename, setFilename] = useState(defaultFilename)
  const [format, setFormat] = useState<DownloadFormat>(formats[0])
  const [split, setSplit] = useState(false)

  // The split checkbox only applies to PDF; a ZIP of per-customer PDFs.
  const splitActive = canSplit && format === "pdf" && split

  // Re-seed inputs on open so the dialog reflects the current default
  // (filters/period may have changed since last open) without leaking
  // last-typed values from a previous session.
  const handleOpenChange = (next: boolean) => {
    if (next) {
      setFilename(defaultFilename)
      setFormat(formats[0])
      setSplit(false)
    }
    setOpen(next)
  }

  const triggerDownload = () => {
    const trimmed = filename.trim()
    if (!trimmed) {
      toast.error("Filename can't be empty.")
      return
    }
    const base = buildHref(format)
    const sep = base.includes("?") ? "&" : "?"
    const splitParam = splitActive ? "&split=customer" : ""
    const url = `${base}${sep}filename=${encodeURIComponent(trimmed)}${splitParam}`
    if (format === "pdf") {
      toast.info(splitActive ? "Generating ZIP…" : "Generating PDF…", {
        duration: 2500,
      })
    }
    window.location.href = url
    setOpen(false)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" className="h-11" disabled={disabled}>
          <Download className="mr-1 h-4 w-4" />
          {triggerLabel}
          <ChevronDown className="ml-1 h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md **:data-[slot=dialog-close]:flex **:data-[slot=dialog-close]:size-11 **:data-[slot=dialog-close]:items-center **:data-[slot=dialog-close]:justify-center">
        <DialogHeader>
          <DialogTitle>Download report</DialogTitle>
          <DialogDescription>
            Choose the filename and file format for this report.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="download-filename" className="text-xs">
              Filename
            </Label>
            <div className="flex items-center gap-2">
              <Input
                id="download-filename"
                className="h-11"
                value={filename}
                onChange={(e) => setFilename(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault()
                    triggerDownload()
                  }
                }}
                autoFocus
              />
              <span className="text-muted-foreground text-sm">
                .{splitActive ? "zip" : format}
              </span>
            </div>
            {splitActive && (
              <p className="text-muted-foreground text-xs">
                ZIP of one PDF per customer —{" "}
                <span className="font-mono">
                  {filename.trim() ? `${filename.trim()}_` : ""}
                  &lt;customer&gt;.pdf
                </span>{" "}
                inside.
              </p>
            )}
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Format</Label>
            <div className="flex flex-wrap gap-2">
              {formats.map((f) => (
                <label
                  key={f}
                  className={`flex min-h-11 cursor-pointer items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm ${
                    format === f
                      ? "border-primary bg-primary/5"
                      : "border-input"
                  }`}
                >
                  <input
                    type="radio"
                    name="download-format"
                    value={f}
                    checked={format === f}
                    onChange={() => {
                      setFormat(f)
                      // Split only applies to PDF; clear it on switch away.
                      if (f !== "pdf") setSplit(false)
                    }}
                    className="accent-primary"
                  />
                  {FORMAT_LABEL[f]}
                </label>
              ))}
            </div>
          </div>

          {canSplit && format === "pdf" && (
            <label className="flex min-h-11 cursor-pointer items-center gap-2 py-1.5 text-sm">
              <Checkbox
                checked={split}
                onCheckedChange={(checked) => setSplit(checked === true)}
              />
              Split into one file per customer
            </label>
          )}

          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              className="h-11"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button className="h-11" onClick={triggerDownload}>
              <Download className="mr-1 h-4 w-4" />
              Download
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
